import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CheckCircle2,
  XCircle,
  Clock,
  Trophy,
  ArrowRight,
  Zap,
  Sparkles,
  Award,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
const OPT_COLORS = [
  { bg: "bg-cyan-500", text: "text-black", label: "A" },
  { bg: "bg-fuchsia-500", text: "text-white", label: "B" },
  { bg: "bg-pink-500", text: "text-white", label: "C" },
  { bg: "bg-emerald-500", text: "text-black", label: "D" },
  { bg: "bg-amber-500", text: "text-black", label: "E" },
  { bg: "bg-violet-500", text: "text-white", label: "F" },
];

export default function PlaySession() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [result, setResult] = useState(null); // last answer feedback
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const submittedRef = useRef(false); // prevent double submit on auto-expire
  const timerRef = useRef(null);

  // Fetch state
  const refresh = async () => {
    try {
      const { data } = await api.get(`/play/sessions/${sessionId}/state`);
      submittedRef.current = false;
      setResult(null);
      setSnapshot(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Timer based on server-aware shown_at
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!snapshot?.current || result || submitting) {
      setTimeLeft(null);
      return;
    }
    const cur = snapshot.current;
    if (!cur.shown_at || !cur.server_time) return;
    const offset = Date.now() - new Date(cur.server_time).getTime();
    const localShownAt = new Date(cur.shown_at).getTime() + offset;
    const limitMs = cur.question.time_limit * 1000;
    const tick = () => {
      const remaining = Math.max(0, localShownAt + limitMs - Date.now());
      setTimeLeft(remaining / 1000);
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        if (!submittedRef.current) {
          submittedRef.current = true;
          submit(null);
        }
      }
    };
    tick();
    timerRef.current = setInterval(tick, 100);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot, result, submitting]);

  const submit = async (option_id) => {
    if (submitting || !snapshot?.current) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/play/sessions/${sessionId}/answer`, {
        question_id: snapshot.current.question.id,
        option_id,
      });
      setResult(data.result);
      // Persist new state but keep result visible
      const newState = data.state;
      // Auto-advance after 3s
      setTimeout(async () => {
        // If session reaches a terminal-or-interstitial state, render directly
        if (
          newState.status === "completed" ||
          newState.status === "awaiting_next" ||
          newState.status === "expired"
        ) {
          setSnapshot(newState);
          setResult(null);
          setSubmitting(false);
          return;
        }
        // Otherwise re-fetch /state to register shown_at on next question
        await refresh();
        setSubmitting(false);
      }, 3000);
    } catch (e) {
      toast.error(formatApiError(e));
      setSubmitting(false);
    }
  };

  const advanceQuiz = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/play/sessions/${sessionId}/advance-quiz`);
      setSnapshot(data.state);
      setResult(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!snapshot) {
    return (
      <FullscreenWrap>
        <div className="text-cyan-200/70 font-display tracking-widest">CHARGEMENT...</div>
      </FullscreenWrap>
    );
  }

  // --- Expired (session deadline blown OR challenge force-finalized while in flight)
  if (snapshot.status === "expired") {
    return (
      <FullscreenWrap>
        <div className="max-w-md mx-auto text-center" data-testid="play-expired">
          <Clock className="w-14 h-14 mx-auto text-orange-300 mb-3" />
          <h1 className="font-display text-3xl font-black uppercase mb-2">
            Session expirée
          </h1>
          <p className="text-white/60 mb-7 text-sm">
            Le temps imparti pour terminer ce quiz est dépassé. Ton score
            partiel est enregistré, mais tu ne peux plus répondre.
          </p>
          <div className="panel rounded-2xl p-6 mb-7">
            <div className="text-xs uppercase tracking-widest text-white/40 font-mono-stat mb-1">
              Score partiel
            </div>
            <div className="font-display font-black text-4xl text-orange-300">
              {snapshot.total_score}
            </div>
          </div>
          <button
            data-testid="play-expired-back-button"
            onClick={() => navigate("/mes-codes")}
            className="px-5 py-3 rounded-xl bg-cyan-400 text-black font-display font-bold uppercase tracking-widest active:scale-95 transition-all"
          >
            Retour à mes codes
          </button>
        </div>
      </FullscreenWrap>
    );
  }

  // --- Awaiting next quiz (multi-quiz challenges only)
  if (snapshot.status === "awaiting_next") {
    const idx = snapshot.awaiting_after_quiz_idx ?? 0;
    const done = snapshot.quizzes?.[idx];
    const totalQ = done?.question_count || 0;
    const correct = done?.correct_answers || 0;
    const completedCount = (snapshot.quizzes || []).filter((q) => q.status === "done").length;
    const totalQuizzes = snapshot.quizzes?.length || 0;
    return (
      <FullscreenWrap>
        <div className="max-w-md mx-auto text-center" data-testid="play-interstitial">
          <Award className="w-16 h-16 mx-auto text-cyan-300 mb-3" />
          <div className="text-[10px] uppercase tracking-[0.3em] font-mono-stat text-cyan-300 mb-1">
            Quiz {idx + 1} / {totalQuizzes} terminé
          </div>
          <h1
            data-testid="play-interstitial-title"
            className="font-display text-3xl font-black uppercase mb-2"
          >
            {done?.title || `Quiz ${idx + 1}`}
          </h1>
          <p className="text-white/65 text-sm mb-6">
            Beau travail. Voici ton score sur ce quiz avant de passer au suivant.
          </p>
          <div className="grid grid-cols-3 gap-2 mb-7">
            <Stat
              label="Score quiz"
              value={done?.score ?? 0}
              accent="cyan"
              testid="interstitial-score"
            />
            <Stat
              label="Bonnes rép."
              value={`${correct}/${totalQ}`}
              accent="emerald"
              testid="interstitial-correct"
            />
            <Stat
              label="Score total"
              value={snapshot.total_score}
              accent="amber"
              testid="interstitial-total"
            />
          </div>
          <div className="text-xs text-white/40 font-mono-stat uppercase tracking-widest mb-5">
            {completedCount} quiz terminé{completedCount > 1 ? "s" : ""} ·{" "}
            {totalQuizzes - completedCount} restant{totalQuizzes - completedCount > 1 ? "s" : ""}
          </div>
          <button
            data-testid="play-next-quiz-button"
            onClick={advanceQuiz}
            disabled={submitting}
            className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl bg-cyan-400 hover:bg-cyan-300 text-black font-display font-bold uppercase tracking-widest active:scale-95 disabled:opacity-50 transition-all glow-primary"
          >
            <ArrowRight className="w-5 h-5" />
            {submitting ? "…" : "Quiz suivant ?"}
          </button>
        </div>
      </FullscreenWrap>
    );
  }

  // --- Completed
  if (snapshot.status === "completed") {
    // v2.42 — Qualifier / no-cash challenges: hide the cash-claim flow entirely.
    // The reward is the qualification itself (surfaced via /me/qualifications).
    const ch = snapshot.challenge || {};
    const isQualifier = ch.reward_type === "qualification";
    const hasCashPrize = Number(ch.prize_pool_base ?? ch.prize_pool ?? 0) > 0 && !isQualifier;
    const isFinalized = ch.status === "finalized";
    return (
      <FullscreenWrap>
        <div className="max-w-md mx-auto text-center" data-testid="play-completed">
          <Trophy className="w-16 h-16 mx-auto text-amber-300 mb-3" />
          <div className="text-xs uppercase tracking-[0.3em] font-mono-stat text-fuchsia-300 mb-2">
            {isQualifier ? "Participation terminée" : "Défi terminé"}
          </div>
          <h1 className="font-display text-4xl font-black uppercase mb-2">
            {isQualifier ? "Merci !" : "Bravo"}
          </h1>
          <p className="text-white/60 mb-7 text-sm">
            {isQualifier && !isFinalized
              ? "Classement provisoire — les qualifications seront confirmées à la fermeture du quiz."
              : isQualifier && isFinalized && snapshot.is_winner
              ? `Bravo ! Tu es classé #${snapshot.final_rank} — vérifie ta qualification dans « Mes finales ».`
              : isQualifier && isFinalized
              ? `Tu termines #${snapshot.final_rank}. Tu n'es pas qualifié cette fois-ci.`
              : hasCashPrize && isFinalized && snapshot.is_winner
              ? `Tu es classé #${snapshot.final_rank} ! Tu peux réclamer ta part de la cagnotte.`
              : hasCashPrize && !isFinalized
              ? "Classement provisoire — les gains seront confirmés à la fermeture du quiz."
              : ch.status === "finalized"
              ? "Ton score est enregistré. Tu n'es pas dans le top — bonne chance pour la prochaine !"
              : "Classement provisoire — les résultats seront figés à la fermeture du quiz."}
          </p>
          <div className="panel rounded-2xl p-6 mb-7">
            <div className="text-xs uppercase tracking-widest text-white/40 font-mono-stat mb-1">
              Ton score
            </div>
            <div className="font-display font-black text-5xl text-cyan-300 neon-text-primary">
              {snapshot.total_score}
            </div>
          </div>
          {/* v2.42 — Qualifier winners get a big "Voir ma finale" CTA instead
              of the cash-claim form. */}
          {isQualifier && isFinalized && snapshot.is_winner && (
            <div
              data-testid="play-qualifier-winner-banner"
              className="rounded-2xl p-5 mb-6 border border-cyan-400/50 bg-gradient-to-br from-cyan-400/12 via-fuchsia-400/6 to-transparent text-left"
            >
              <div className="text-xs uppercase tracking-widest font-mono-stat text-cyan-300 mb-1">
                ✓ Tu es qualifié pour la finale
              </div>
              <p className="text-sm text-white/75 mb-3">
                Ta place est réservée automatiquement.
              </p>
              <button
                data-testid="play-see-my-finale-cta"
                onClick={() => (window.location.href = "/mes-qualifications")}
                className="w-full px-4 py-3 rounded-xl bg-cyan-400 text-black font-display font-bold uppercase tracking-wider hover:bg-cyan-300 active:scale-95 transition-all"
              >
                Voir ma finale
              </button>
            </div>
          )}
          {hasCashPrize && snapshot.is_winner && !snapshot.claimed && (
            <ClaimForm
              sessionId={sessionId}
              winnersCount={snapshot.winners_count}
              rank={snapshot.final_rank}
              pool={snapshot.challenge?.prize_pool}
              onClaimed={refresh}
            />
          )}
          {snapshot.claimed && (
            <div
              data-testid="play-claimed-confirmation"
              className="panel rounded-2xl p-5 mb-6 border border-emerald-400/40 bg-emerald-400/10 text-left"
            >
              <div className="text-xs uppercase tracking-widest font-mono-stat text-emerald-300 mb-1">
                ✓ Réclamation envoyée
              </div>
              <p className="text-sm text-white/70">
                Votre demande de paiement a bien été transmise. Elle sera vérifiée par
                l&apos;équipe OTRAQUIZZ avant virement.
              </p>
              <p className="text-xs text-white/50 font-mono-stat mt-2">
                Statut :{" "}
                <span className="text-amber-300 font-bold">En attente de paiement</span>
              </p>
            </div>
          )}
          <div className="space-y-2">
            {snapshot.quizzes.map((qz) => (
              <div key={qz.id} className="flex items-center justify-between text-sm panel rounded-xl px-4 py-2">
                <span className="font-display font-bold truncate">{qz.title}</span>
                <span className="font-mono-stat text-cyan-300">{qz.score} pts</span>
              </div>
            ))}
          </div>
          <button
            data-testid="play-back-home-button"
            onClick={() => navigate("/")}
            className="mt-8 px-5 py-3 rounded-xl bg-cyan-400 text-black font-display font-bold uppercase tracking-widest active:scale-95 transition-all"
          >
            Retour à l&apos;accueil
          </button>
          {ch?.id && !isFinalized && (
            <button
              data-testid="play-see-provisional-leaderboard-button"
              onClick={() => navigate(`/leaderboard/${ch.id}`)}
              className="mt-3 px-5 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-cyan-400/40 text-cyan-200 font-display font-bold uppercase tracking-widest active:scale-95 transition-all"
            >
              Voir le classement provisoire
            </button>
          )}
        </div>
      </FullscreenWrap>
    );
  }

  // --- Result feedback (after answer)
  if (result) {
    return (
      <FullscreenWrap>
        <div className="text-center max-w-sm mx-auto" data-testid="play-result">
          {result.is_correct ? (
            <>
              <CheckCircle2 className="w-20 h-20 text-emerald-400 mx-auto mb-4" />
              <h2 className="font-display text-3xl font-black uppercase text-emerald-300 neon-text-primary">
                Bonne réponse !
              </h2>
              <div className="text-3xl font-display font-bold mt-3">+ {result.earned} pts</div>
            </>
          ) : (
            <>
              <XCircle className="w-20 h-20 text-pink-400 mx-auto mb-4" />
              <h2 className="font-display text-3xl font-black uppercase text-pink-300 neon-text-accent">
                Raté
              </h2>
              <div className="text-base text-white/60 mt-1">
                {result.elapsed_seconds === null
                  ? "Pas de réponse à temps"
                  : "Pas la bonne réponse cette fois"}
              </div>
            </>
          )}
          <div className="panel rounded-2xl px-6 py-4 mt-7 flex items-center gap-6 justify-center flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-widest text-white/40 font-mono-stat">Score total</div>
              <div className="font-display font-black text-2xl text-cyan-300">{snapshot.total_score + result.earned}</div>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div>
              <div className="text-xs uppercase tracking-widest text-white/40 font-mono-stat">Temps</div>
              <div className="font-display font-black text-2xl text-fuchsia-300">
                {result.elapsed_seconds !== null ? `${result.elapsed_seconds.toFixed(1)}s` : "—"}
              </div>
            </div>
          </div>
          <p className="text-xs uppercase tracking-widest text-white/40 mt-6 font-mono-stat">
            Question suivante dans quelques instants...
          </p>
        </div>
      </FullscreenWrap>
    );
  }

  // --- Question
  const cur = snapshot.current;
  if (!cur) {
    return (
      <FullscreenWrap>
        <div className="text-white/50 font-mono-stat uppercase">Préparation...</div>
      </FullscreenWrap>
    );
  }

  const q = cur.question;
  const total = q.total;
  const timeRemaining = timeLeft ?? q.time_limit;
  const pct = Math.max(0, Math.min(100, (timeRemaining / q.time_limit) * 100));
  const challengeClosingNow =
    snapshot.challenge?.status === "pending_finalization";

  return (
    <FullscreenWrap>
      {challengeClosingNow && (
        <div
          data-testid="play-pending-finalization-banner"
          className="px-4 py-2 text-center text-xs sm:text-sm bg-orange-500/20 border-b border-orange-400/40 text-orange-100 font-mono-stat"
        >
          Le défi est fermé aux nouvelles participations, mais tu peux terminer ta session en cours.
        </div>
      )}
      <header className="px-5 py-4 flex items-center justify-between border-b border-white/5 panel">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-cyan-400" />
          <span className="font-display font-black uppercase tracking-tighter text-sm">
            Otra<span className="text-cyan-400">Quizz</span>
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs font-mono-stat">
          <span className="text-white/40 uppercase tracking-widest">
            {cur.quiz_title}
          </span>
          <span className="text-cyan-300 uppercase tracking-widest">
            Score : {snapshot.total_score}
          </span>
        </div>
      </header>

      <div className="flex-1 flex flex-col px-5 py-5">
        <div className="flex items-center justify-between mb-3 text-xs font-mono-stat uppercase tracking-widest">
          <span className="text-cyan-300">
            Q{q.index + 1}/{total}
          </span>
          <span className={`flex items-center gap-1 ${timeRemaining < 5 ? "text-pink-300" : "text-white/60"}`}>
            <Clock className="w-3 h-3" />
            {timeRemaining.toFixed(1)}s · {q.points} pts max
          </span>
        </div>

        {/* progress */}
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mb-5">
          <div
            className={`h-full transition-all ${timeRemaining < 5 ? "bg-pink-400" : "bg-cyan-400"}`}
            style={{ width: `${pct}%` }}
          />
        </div>

        <div className="panel rounded-2xl p-5 mb-5">
          <h2 className="font-display text-xl sm:text-2xl font-bold leading-snug" data-testid="play-question-text">
            {q.text}
          </h2>
        </div>

        <div className="grid grid-cols-2 gap-3 flex-1">
          {q.options.map((opt, i) => {
            const c = OPT_COLORS[i] || OPT_COLORS[0];
            return (
              <button
                key={opt.id}
                data-testid={`play-option-${i}`}
                disabled={submitting}
                onClick={() => submit(opt.id)}
                className={`relative ${c.bg} ${c.text} rounded-3xl font-display font-black text-lg sm:text-2xl active:scale-95 transition-all min-h-[120px] flex items-center justify-center p-4 shadow-[inset_0_0_30px_rgba(0,0,0,0.3)] disabled:opacity-40`}
              >
                <span className="absolute top-2 left-3 text-xs opacity-50 font-mono-stat">
                  {c.label}
                </span>
                <span className="text-center px-2">{opt.text}</span>
              </button>
            );
          })}
        </div>
      </div>
    </FullscreenWrap>
  );
}

function FullscreenWrap({ children }) {
  return (
    <div className="min-h-screen relative flex flex-col">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-fuchsia-500/10" />
      <div className="relative flex-1 flex flex-col">{children}</div>
    </div>
  );
}

function Stat({ label, value, accent = "cyan", testid }) {
  const accents = {
    cyan: "text-cyan-300 border-cyan-400/30",
    emerald: "text-emerald-300 border-emerald-400/30",
    amber: "text-amber-300 border-amber-400/30",
  };
  return (
    <div className={`rounded-2xl border bg-black/40 p-3 ${accents[accent] || accents.cyan}`}>
      <div className="text-[9px] uppercase tracking-widest font-mono-stat text-white/45 mb-1">
        {label}
      </div>
      <div
        data-testid={testid}
        className="font-display font-black text-2xl text-white"
      >
        {value}
      </div>
    </div>
  );
}

function ClaimForm({ sessionId, winnersCount, rank, pool, onClaimed }) {
  const [iban, setIban] = useState("");
  const [name, setName] = useState("");
  const [bic, setBic] = useState("");
  const [loading, setLoading] = useState(false);
  const share = pool && winnersCount ? (pool / winnersCount).toFixed(2) : null;

  // Client-side IBAN sanity check — the backend has the authoritative
  // validation. Strip spaces, uppercase, verify length & alnum ; FR-specific
  // IBANs must be exactly 27 chars.
  const cleanIban = iban.replace(/\s+/g, "").toUpperCase();
  const ibanLooksInvalid =
    cleanIban.length > 0 &&
    (cleanIban.length < 15 ||
      cleanIban.length > 34 ||
      !/^[A-Z0-9]+$/.test(cleanIban) ||
      (cleanIban.startsWith("FR") && cleanIban.length !== 27));

  const submit = async (e) => {
    e.preventDefault();
    if (ibanLooksInvalid) {
      toast.error("L'IBAN semble invalide. Merci de vérifier votre saisie.");
      return;
    }
    setLoading(true);
    try {
      await api.post(`/play/sessions/${sessionId}/claim`, {
        iban: cleanIban,
        full_name: name,
        bic_swift: bic.replace(/\s+/g, "").toUpperCase() || null,
      });
      toast.success("Demande envoyée. L'équipe OTRAQUIZZ te contactera après vérification.");
      onClaimed && onClaimed();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  return (
    <form
      onSubmit={submit}
      data-testid="play-claim-form"
      className="panel rounded-2xl p-5 mb-6 border border-amber-400/40 bg-amber-400/5 text-left space-y-3"
    >
      <div>
        <div className="text-xs uppercase tracking-widest font-mono-stat text-amber-300 mb-1">
          🏆 Réclamer mes gains
        </div>
        <p className="text-sm text-white/70">
          Tu termines <span className="font-mono-stat font-bold text-amber-200">#{rank}</span> sur les{" "}
          <span className="font-mono-stat font-bold">{winnersCount}</span> gagnants
          {share && (
            <>
              {" "}— part estimée :{" "}
              <span className="font-mono-stat font-bold text-amber-200">{share} €</span>
            </>
          )}
          .
        </p>
      </div>
      <label className="block">
        <span className="text-[10px] uppercase tracking-widest font-mono-stat text-white/50">
          Nom du titulaire du compte
        </span>
        <input
          data-testid="claim-name-input"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={120}
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1 outline-none focus:border-amber-400/60 text-white"
          placeholder="Tel qu'inscrit sur ton compte bancaire"
        />
      </label>
      <label className="block">
        <span className="text-[10px] uppercase tracking-widest font-mono-stat text-white/50">IBAN</span>
        <input
          data-testid="claim-iban-input"
          required
          value={iban}
          onChange={(e) => setIban(e.target.value.toUpperCase())}
          minLength={15}
          maxLength={42}
          className={`w-full bg-black/40 border rounded-lg px-3 py-2 mt-1 outline-none text-white font-mono-stat tracking-wider ${
            ibanLooksInvalid ? "border-rose-400/60 focus:border-rose-400" : "border-white/10 focus:border-amber-400/60"
          }`}
          placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
        />
        {ibanLooksInvalid && (
          <span
            data-testid="claim-iban-error"
            className="block text-[10px] text-rose-300 font-mono-stat mt-1"
          >
            L&apos;IBAN semble invalide. Merci de vérifier votre saisie.
          </span>
        )}
      </label>
      <label className="block">
        <span className="text-[10px] uppercase tracking-widest font-mono-stat text-white/50">
          BIC / SWIFT (facultatif)
        </span>
        <input
          data-testid="claim-bic-input"
          value={bic}
          onChange={(e) => setBic(e.target.value.toUpperCase())}
          maxLength={11}
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1 outline-none focus:border-amber-400/60 text-white font-mono-stat tracking-wider"
          placeholder="BNPAFRPPXXX"
        />
        <span className="block text-[10px] text-white/50 font-mono-stat mt-1">
          Facultatif pour la plupart des virements SEPA. À renseigner si votre banque le demande.
        </span>
      </label>
      <button
        data-testid="claim-submit-button"
        disabled={loading || ibanLooksInvalid}
        className="w-full px-5 py-3 rounded-xl bg-amber-400 text-black font-display font-bold uppercase tracking-widest hover:bg-amber-300 active:scale-95 disabled:opacity-50 transition-all"
      >
        {loading ? "Envoi..." : "Réclamer mes gains"}
      </button>
      <p className="text-[10px] text-white/50 font-mono-stat">
        Ces informations servent uniquement au paiement manuel de votre gain. Elles ne
        sont visibles que par l&apos;équipe OTRAQUIZZ.
      </p>
    </form>
  );
}
